import dayjs from "dayjs";

import {
  ACCEPT_AGE_GATE,
  AcceptAgeGateAction,
  CHECK_AGE_GATE,
  CheckAgeGateAction,
} from "redux/action-creators/ageGate";
import { userAgentIsGoogleBot } from "utils/userAgent";

import { AllActions } from "./rootReducer";

/**
 * TOU Version requires manual update to ensure accepted terms are up to date
 */
const TOU_VERSIONS: Record<string, number> = {
  CA: 3,
  US: 3,
};

export type AgeGateState = {
  disabledAgeGate?: boolean;
  termsHaveBeenAccepted?: boolean;
  showingModal: boolean;
};

export const initialState: AgeGateState = {
  showingModal: false,
};

export type AgeGateActions = AcceptAgeGateAction | CheckAgeGateAction;

const STORAGE_KEYS = {
  termsOfUse: {
    country: "acceptedTermsOfUseCountry",
    expiryDate: "acceptedTermsExpiryDate",
    firstVisitDate: "firstVisitDate",
    isAgeVerified: "isAgeVerified",
    version: "acceptedTermsOfUseVersion",
  },
};
const parseStorage = (storage: Storage) => ({
  termsOfUse: {
    country: storage.getItem(STORAGE_KEYS.termsOfUse.country),
    expiryDate: storage.getItem(STORAGE_KEYS.termsOfUse.expiryDate),
    version: storage.getItem(STORAGE_KEYS.termsOfUse.version),
  },
});

const checkTermsAcceptance = (
  countryCode: string,
  storage: ReturnType<typeof parseStorage>
) => {
  if (
    !storage.termsOfUse.country ||
    !storage.termsOfUse.expiryDate ||
    !storage.termsOfUse.version
  ) {
    return false;
  }

  const touVersion = TOU_VERSIONS[countryCode.toUpperCase()];
  const versionsMatch = touVersion === parseInt(storage.termsOfUse.version, 10);
  const countriesMatch =
    countryCode.toLowerCase() === storage.termsOfUse.country.toLowerCase();
  const expired = dayjs().isAfter(dayjs(storage.termsOfUse.expiryDate));

  return versionsMatch && !expired && countriesMatch;
};

const ageGateReducer = (
  state = initialState,
  action: AllActions
): AgeGateState => {
  let disabledAgeGate: boolean;
  let expiryAmount: string;
  let startedOnCompanyUrl: boolean;
  let termsHaveBeenAccepted: boolean;
  let storageItems: Record<string, string>;
  switch (action.type) {
    case ACCEPT_AGE_GATE:
      if (!action.storage) {
        return state;
      }

      expiryAmount = (
        action.rememberUser ? dayjs().add(1, "year") : dayjs().add(1, "hour")
      ).toString();

      storageItems = {
        [STORAGE_KEYS.termsOfUse.country]: action.countryCode.toLowerCase(),
        [STORAGE_KEYS.termsOfUse.version]:
          TOU_VERSIONS[action.countryCode.toUpperCase()] + "",
        [STORAGE_KEYS.termsOfUse.expiryDate]: expiryAmount,
        [STORAGE_KEYS.termsOfUse.isAgeVerified]: "true",
        [STORAGE_KEYS.termsOfUse.firstVisitDate]: new Date().toISOString(),
      };

      Object.keys(storageItems).forEach((key) => {
        if (action.storage) {
          action.storage.setItem(key, storageItems[key]);
        }
      });

      return {
        ...state,
        showingModal: false,
        termsHaveBeenAccepted: true,
      };
    case CHECK_AGE_GATE:
      disabledAgeGate = action.queryString.includes("disable_age_gate");
      startedOnCompanyUrl = new RegExp(
        "^/info/(privacy-policy|terms-of-use|commercial-terms-of-use|jobs|contact|about)/?$"
      ).test(action.currentUrl);

      if (!action.storage) {
        return {
          disabledAgeGate,
          showingModal:
            !disabledAgeGate &&
            !startedOnCompanyUrl &&
            !userAgentIsGoogleBot(action.userAgent),
        };
      }

      termsHaveBeenAccepted = checkTermsAcceptance(
        action.countryCode,
        parseStorage(action.storage)
      );

      return {
        disabledAgeGate,
        showingModal:
          !disabledAgeGate &&
          !termsHaveBeenAccepted &&
          !startedOnCompanyUrl &&
          !userAgentIsGoogleBot(action.userAgent),
        termsHaveBeenAccepted,
      };
    default:
      return state;
  }
};

export default ageGateReducer;
